import React, { useEffect, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject as ChartInject,
  Legend,
  Category,
  ColumnSeries,
  LineSeries,
  DateTime,
  Tooltip,
  StackingColumnSeries,
} from "@syncfusion/ej2-react-charts";
import { PieChart } from "@mui/x-charts/PieChart";
import { QUDA_COLORS } from "../../config/colors";

const ChartCard = ({
  title,
  data,
  primaryXAxis,
  primaryYAxis,
  series,
  pieData,
  borderColor,
  borderWidth = 2,
  borderClass,
  pieTitle = "Order Types",
  /** Lower min-heights for compact dashboards (e.g. admin Quda). */
  mainChartMinHeight = 360,
  pieChartMinHeight = 320,
  /** When true, only the pie/accumulation chart is shown (full width). */
  hideMainChart = false,
  /** When true, only the main chart is shown (full width). */
  hidePieChart = false,
  /** Optional Syncfusion Chart tooltipRender for the main chart. */
  mainChartTooltipRender,
  /** Kept for API compat — not used by MUI PieChart. */
  pieStartAngle = "270",
  pieEndAngle = "90",
  /** Donut inner radius in pixels. */
  pieInnerRadius = 40,
  /** Kept for API compat. */
  pieLegendSettings,
  /** Kept for API compat. */
  pieRadius = "70%",
  /** Legend position: "Bottom" or "Right". */
  pieLegendPosition = "Bottom",
  /** When true with hideMainChart, root and pie panel stretch to fill a flex parent. */
  fillParentHeight = false,
  /** Used as the pie series id — must be unique per page when multiple ChartCards appear. */
  chartId = "appointment-type-chart",
  /** Fixed height for the pie card — overrides pieChartMinHeight. */
  pieChartHeight,
  /** Called when a pie arc is clicked: (event, identifier, item) => void. Enables pointer cursor. */
  onPieItemClick,
  /** Controlled highlight for the pie — { seriesId, dataIndex } or undefined. Drives the explode effect. */
  selectedPieItem,
}) => {
  const mainChartRef = useRef(null);
  const mainChartContainerRef = useRef(null);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    if (!mainChartContainerRef.current) return;
    const observer = new ResizeObserver(() => {
      mainChartRef.current?.refresh();
    });
    observer.observe(mainChartContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const rootIsPieOnly = hideMainChart && !hidePieChart;
  const rootClassName =
    fillParentHeight && rootIsPieOnly
      ? "flex flex-col flex-1 min-h-0 h-full w-full"
      : `grid gap-4 mt-4 ${
          hideMainChart || hidePieChart ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-12"
        }`;

  const pieOuterClassBase = hideMainChart ? "" : "lg:col-span-3";
  const pieOuterClassFill =
    fillParentHeight && rootIsPieOnly ? "flex-1 min-h-0" : "";

  const pieContainerStyle = {
    ...(pieChartHeight != null
      ? { height: pieChartHeight, overflow: "hidden" }
      : fillParentHeight && rootIsPieOnly
        ? { flex: 1, minHeight: 0 }
        : { minHeight: pieChartMinHeight }),
    ...(borderClass ? {} : { border: `${borderWidth}px solid ${borderColor || QUDA_COLORS.TEAL_DARK}` }),
    borderRadius: "8px",
  };

  const muiPieData = pieData.map((item, i) => ({
    id: i,
    value: item.y,
    label: item.x,
    color: item.color,
  }));

  const pieLegendSlotProps =
    pieLegendPosition === "Right"
      ? { direction: "column", position: { vertical: "middle", horizontal: "right" } }
      : { direction: "row", position: { vertical: "bottom", horizontal: "middle" } };

  const pieMargin =
    pieLegendPosition === "Right"
      ? { top: 8, bottom: 8, left: 8, right: 120 }
      : { top: 8, bottom: 56, left: 8, right: 8 };

  return (
    <div className={rootClassName}>
      {/* Main Chart */}
      {!hideMainChart && (
        <div
          ref={mainChartContainerRef}
          className={`col-span-1 ${hidePieChart ? "" : "lg:col-span-9"} bg-white text-black shadow rounded-xl p-1 w-full flex flex-col ${borderClass || ""}`}
          style={{
            minHeight: mainChartMinHeight,
            ...(borderClass ? {} : { border: `${borderWidth}px solid ${borderColor || QUDA_COLORS.TEAL_DARK}` }),
            borderRadius: "8px",
          }}
        >
          <h3 className="text-lg font-bold text-center mb-0 text-black">
            {title}
          </h3>
          <ChartComponent
            ref={mainChartRef}
            title=""
            titleStyle={{ fontSize: "18px", fontWeight: "bold", margin: "0px" }}
            primaryXAxis={primaryXAxis}
            primaryYAxis={primaryYAxis}
            axes={[
              {
                name: "RevenueAxis",
                title: "Revenue ($)",
                opposedPosition: true,
                labelFormat: "${value}",
                majorGridLines: { width: 0 },
              },
            ]}
            tooltip={{ enable: true }}
            {...(typeof mainChartTooltipRender === "function"
              ? { tooltipRender: mainChartTooltipRender }
              : {})}
            height="100%"
            width="100%"
            legendSettings={{ visible: true }}
          >
            <ChartInject
              services={[
                ColumnSeries,
                StackingColumnSeries,
                LineSeries,
                Legend,
                Category,
                DateTime,
                Tooltip,
              ]}
            />
            <SeriesCollectionDirective>
              {series.map((s, index) => (
                <SeriesDirective key={index} {...s} columnWidth={3} width={3} />
              ))}
            </SeriesCollectionDirective>
          </ChartComponent>
        </div>
      )}

      {!hidePieChart && (
        <div
          className={`col-span-1 ${pieOuterClassBase} ${pieOuterClassFill} bg-white text-black shadow rounded-xl p-2 flex flex-col ${borderClass || ""}`}
          style={pieContainerStyle}
        >
          <h3 className="text-lg font-bold text-center mb-0 text-black flex-shrink-0">
            {pieTitle}
          </h3>
          <div className="flex-1 min-h-0 w-full min-w-0">
            <PieChart
              series={[
                {
                  id: chartId,
                  data: muiPieData,
                  innerRadius: typeof pieInnerRadius === "number" ? pieInnerRadius : 40,
                  cornerRadius: 6,
                  highlightScope: { fade: "global", highlight: "item" },
                  faded: { opacity: 0.4 },
                },
              ]}
              highlightedItem={selectedPieItem ?? { seriesId: '__none__', dataIndex: -1 }}
              onItemClick={onPieItemClick ?? undefined}
              slotProps={{
                legend: {
                  ...pieLegendSlotProps,
                  itemMarkWidth: 12,
                  itemMarkHeight: 12,
                  markGap: 4,
                  itemGap: 8,
                  labelStyle: { fontSize: 9 },
                },
              }}
              margin={pieMargin}
              sx={{
                width: "100%",
                height: "100%",
                "& .MuiPieArc-root": {
                  transition: "transform 0.2s ease, filter 0.2s ease, opacity 0.2s ease",
                  ...(onPieItemClick ? { cursor: "pointer" } : {}),
                },
                "& .MuiPieArc-highlighted": {
                  transform: "scale(1.1)",
                  transformBox: "fill-box",
                  transformOrigin: "center center",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.35))",
                },
                "& .MuiPieArc-faded": {
                  transform: "scale(0.7)",
                  transformBox: "fill-box",
                  transformOrigin: "center center",
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartCard;
